import SelectAccountImage from '@assets/images/account-select.png'
import PlusIcon from '@images/icons/transaction-type-plus-icon.svg?react'
import { formatCurrencyForDisplay } from '@utils'
import cn from 'classnames'
import { PaymentMethods, useCurrencies } from 'mmfintech-backend-api'
import type { AccountBalanceResponse, PaymentOption, PaymentOptionResponse } from 'mmfintech-backend-api/types'
import { checkSingleValue, formatFloat, getCurrencySymbol, isValidFunction, tr, useDebounce } from 'mmfintech-commons'
import { type Dispatch, type SetStateAction, useEffect, useMemo, useRef, useState } from 'react'
import { CoreSelect } from '../CoreSelect'
import { type AmountOptionWithHelper, CompactAmountDropdown } from './CompactAmountDropdown'

import './corePaymentInput.scss'

interface CorePaymentInputProps {
  value?: string
  name?: string
  selectedOption?: AccountBalanceResponse | { value: string; label: string }
  setSelectedAccount?: Dispatch<SetStateAction<AccountBalanceResponse>> | ((arg: AccountBalanceResponse) => void)
  onChange?: any
  paymentOption?: PaymentOption | PaymentOptionResponse
  error?: string
  setError?: (arg: any) => void
  dataTest: string
  options?: AccountBalanceResponse[] | { value: string; label: string }[]
  disableAmountChange?: boolean
  disableMaxAmount?: boolean
  disableMinAmount?: boolean
  hideAmount?: boolean
  required?: boolean
  isLoading?: boolean
  hint?: string
  minInputAmount?: number
  maxInputAmount?: number
  enableMinMaxLabel?: boolean
  hideCurrencySymbol?: boolean
  type?: 'account' | 'currency' | 'label'
  enableAccountCreation?: boolean
  label?: string
  defaultCurrency?: string
  extraOptions?: any[] | null
  currencyOptions?: string[]
  isFocusInputOnClick?: boolean
  enableMaxAmountButton?: boolean
  currencyCodeOverride?: string
  isMaxAmountVisible?: boolean
  amountOptions?: Array<AmountOptionWithHelper>
  selectedAmountOption?: string
  onAmountSelect?: (value: string) => void
  compactAmountDropdown?: boolean
  noOptionsMessage?: string
  noOptionsAction?: { label: string; onClick: () => void }
}

export const CorePaymentInput = (props: CorePaymentInputProps) => {
  const {
    value,
    name,
    options,
    selectedOption,
    onChange,
    paymentOption,
    error,
    setError,
    dataTest,
    setSelectedAccount,
    hint,
    minInputAmount,
    maxInputAmount,
    hideAmount = false,
    disableMaxAmount = false,
    disableMinAmount = false,
    required = false,
    disableAmountChange = false,
    isLoading = false,
    hideCurrencySymbol = false,
    type = 'account',
    enableAccountCreation = false,
    enableMaxAmountButton = false,
    label,
    defaultCurrency,
    extraOptions = null,
    currencyOptions,
    // focus input when click on the  main div wrapper
    isFocusInputOnClick = false,
    currencyCodeOverride,
    isMaxAmountVisible = true,
    amountOptions,
    selectedAmountOption,
    onAmountSelect,
    compactAmountDropdown = false,
    noOptionsMessage,
    noOptionsAction
  } = props

  const [isValueChanged, setIsValueChanged] = useState(false)
  const currencyCode: string = currencyCodeOverride
    ? currencyCodeOverride
    : selectedOption
      ? type === 'account' && 'currencyCode' in selectedOption
        ? selectedOption?.currencyCode
        : 'value' in selectedOption && selectedOption?.value
      : ''
  const { getCurrencyPrecision } = useCurrencies()
  const decimals = getCurrencyPrecision(currencyCode)
  const inputRef = useRef<HTMLInputElement>(null)

  const isCryptoPaymentMethod = paymentOption?.paymentMethod === PaymentMethods.CRYPTO || hideCurrencySymbol
  const { minAmount: paymentMinAmount, maxAmount: paymentMaxAmount, overdraftMaxAmount } = paymentOption || ({} as any)
  const minAmount = minInputAmount || paymentMinAmount
  const maxAmount = isMaxAmountVisible ? (paymentMaxAmount || maxInputAmount) + (Number(overdraftMaxAmount) || 0) : 0

  const handleCheckMinMax = useDebounce(() => {
    setIsValueChanged(true)
  }, 500)

  const minMaxError = useMemo(() => {
    setIsValueChanged(false)
    return {
      isMaxError: maxAmount && value ? maxAmount < Number(value) : false,
      isMinError: minAmount && value ? minAmount > Number(value) : false
    }
  }, [isValueChanged])

  useEffect(() => {
    handleCheckMinMax()
  }, [value, minAmount, maxAmount])

  const handleAmountChange = event => {
    if (isValidFunction(onChange)) {
      const val = checkSingleValue(event.target.value, {
        validation: 'float',
        decimals
      }).replace(/^0+/g, '0')

      name ? onChange(name, val) : onChange(val)
      event.target.value = val // replace inner input state with the validated one
      setError?.(null)
    }
  }

  const handleInsertFullMaxAmount = () => {
    if (isValidFunction(onChange)) {
      name ? onChange(name, maxAmount) : onChange(maxAmount)
      setError?.(null)
    }
  }

  return (
    <div className='core-payment-input-container'>
      <div
        key={name}
        className={cn('core-payment-input-wrapper', {
          error: Boolean(error) || minMaxError?.isMinError || minMaxError?.isMaxError,
          'no-amount': hideAmount
        })}
        data-test={dataTest}
        onClick={() => isFocusInputOnClick && inputRef?.current?.focus()}
      >
        {type !== 'label' ? (
          <div className='core-payment-select-wrapper'>
            <CoreSelect
              data-test='all-accounts'
              LeftIcon={<img src={SelectAccountImage} width={30} alt='' />}
              type={type}
              label={
                isLoading
                  ? tr('FRONTEND.MONEY_INPUT.LOADING', 'Loading ...')
                  : type === 'account'
                    ? tr('FRONTEND.BANKING.SCAN_AND_PAY.TIPS.TITLE_3', 'Select account')
                    : tr('FRONTEND.MONEY_INPUT.SELECT_CURRENCY', 'Select currency')
              }
              options={options}
              value={selectedOption}
              onChange={(_, selectedOption) => setSelectedAccount(selectedOption)}
              isAccountsLoading={isLoading}
              enableAccountCreation={enableAccountCreation}
              extraOptions={extraOptions}
              currencyOptions={currencyOptions}
              noOptionsMessage={noOptionsMessage}
              noOptionsAction={noOptionsAction}
            />
          </div>
        ) : (
          <div className='core-payment-select-wrapper label'>
            <span>{label}</span>
          </div>
        )}
        {!hideAmount && (
          <div
            className={cn('payment-input-container', {
              limits: maxAmount > 0 || minAmount > 0,
              'amount-dropdown': amountOptions
            })}
          >
            <div className='input-inner'>
              {/* {!isCryptoPaymentMethod && (
                  <h3 data-test='currencyCode'>{getCurrencySymbol(formatCurrencyForDisplay(currencyCode || defaultCurrency))}</h3>
                )} */}

              <div className='testing'>
                <div className='inline-editor'>
                  <div className='inline-editor'>
                    <label htmlFor={name || 'value'} defaultValue={value}>
                      {value}
                    </label>
                  </div>
                  {!disableAmountChange && (
                    <input
                      ref={inputRef}
                      type='text'
                      id={name || 'value'}
                      name={name || 'value'}
                      onChange={event => {
                        handleAmountChange(event)
                      }}
                      className='inline-editor__control'
                      value={value}
                      autoFocus
                      required={required}
                      autoComplete='off'
                    />
                  )}
                </div>
              </div>

              {!isCryptoPaymentMethod && (
                <span data-test='currencyCode' className='currency-code'>
                  {formatCurrencyForDisplay(currencyCode || defaultCurrency)}
                </span>
              )}
            </div>

            {amountOptions && compactAmountDropdown && (
              <CompactAmountDropdown
                options={amountOptions}
                selectedValue={selectedAmountOption}
                onSelect={(value: string) => {
                  onAmountSelect?.(value)
                }}
                isLoading={isLoading}
              />
            )}

            {!amountOptions && maxAmount > 0 && enableMaxAmountButton && (
              <div
                className='add-max-button'
                onClick={() => {
                  enableMaxAmountButton && handleInsertFullMaxAmount()
                }}
              >
                <PlusIcon />
                <span data-test='value-boundary'>
                  <div key={`max${maxAmount}`}>
                    <span className='title'>{tr('FRONTEND.MONEY_INPUT.LABEL_ADD_MAX', 'Add max')}</span>
                  </div>
                </span>
              </div>
            )}
          </div>
        )}
      </div>
      {hint && (
        <div className={cn('core-payment-hint-wrapper')}>
          <span>{hint}</span>
        </div>
      )}

      <div className='core-payment-footer'>
        <div className='core-payment-limits-wrapper'>
          {minAmount > 0 && !disableMinAmount && (
            <span
              data-test='value-boundary'
              className={cn('value-boundary', {
                'min-max-error': minMaxError?.isMinError
              })}
            >
              <div key={`min${minAmount}`}>
                <span className='title'>{tr('FRONTEND.MONEY_INPUT.LABEL_MIN', 'Min')}</span>
                <span className='content'>
                  {!isCryptoPaymentMethod && getCurrencySymbol(formatCurrencyForDisplay(currencyCode))}{' '}
                  {formatFloat(minAmount, decimals)}
                </span>
              </div>
            </span>
          )}
          {maxAmount > 0 && !disableMaxAmount && (
            <span
              data-test='value-boundary'
              className={cn('value-boundary', {
                'min-max-error': minMaxError?.isMaxError
              })}
            >
              <div key={`max${maxAmount}`}>
                <span className='title'>
                  {tr('FRONTEND.MONEY_INPUT.LABEL_MAX_AVAILABLE', 'Max available before fees')}
                </span>
                <span className='content'>
                  {!isCryptoPaymentMethod && getCurrencySymbol(formatCurrencyForDisplay(currencyCode))}{' '}
                  {formatFloat(maxAmount, decimals)}
                </span>
              </div>
            </span>
          )}
        </div>
        {error && (
          <div className={cn('core-payment-hint-wrapper', { error: error })}>
            <span>{error}</span>
          </div>
        )}
      </div>
    </div>
  )
}
